/**
 * DriftingScore class generates scores between 1-10 with controlled randomness.
 * The score will not jump more than 3 points from the last generated score.
 */
class DriftingScore {
  constructor(initialScore = 5) {
    // Validate initial score is within range
    if (initialScore < 1 || initialScore > 10) {
      throw new Error('Initial score must be between 1 and 10');
    }
    
    this.lastScore = initialScore;
  }

  /**
   * Generates a new score that drifts from the last score by at most 3 points.
   * The direction (up or down) is random, but the magnitude is constrained.
   * 
   * @returns {number} A score between 1 and 10
   */
  generate() {
    // Calculate the possible range for the next score
    const minScore = Math.max(1, this.lastScore - 3);
    const maxScore = Math.min(10, this.lastScore + 3);
    
    // Generate a random score within the constrained range
    const newScore = Math.floor(Math.random() * (maxScore - minScore + 1)) + minScore;
    
    // Update the last score
    this.lastScore = newScore;
    
    return newScore;
  }

  /**
   * Gets the last generated score
   * 
   * @returns {number} The last generated score
   */
  getLastScore() {
    return this.lastScore;
  }

  /**
   * Resets the score to a new initial value
   * 
   * @param {number} newInitialScore - The new initial score (1-10)
   */
  reset(newInitialScore = 5) {
    if (newInitialScore < 1 || newInitialScore > 10) {
      throw new Error('Initial score must be between 1 and 10');
    }
    
    this.lastScore = newInitialScore;
  }
}

export default DriftingScore;
